import { FC, useEffect, useState } from 'react';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../../contexts/TourContext';
import { useTranslatedSteps } from '../stepsTour';
import { convertPersianToEnglishNumbers, formatPersianNumber, toPersianDigits } from '../../utils/validation';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import api from '../../services/api';
import { useError } from '../../contexts/ErrorContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { format as formatJalali } from 'date-fns-jalali';
import { enqueueSnackbar } from 'notistack';
import VideoProvider from '../../components/VideoProvider';
import { videoUrls, videoTitles } from '../../utils/videoUrls';

//* for auto-response list
interface AutoResponseEntry {
  id: number;
  message_type: string;
  condition_type: string;
  trigger_value: string;
  response_message: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AUTO_RESPONSE_CONTAINS_EXAMPLES = [
  'قیمت این محصول چقدر است؟',
  'لیست قیمت‌ها را ارسال می‌کنید؟',
  'قیمت همکاری دارید؟',
];

const AUTO_RESPONSE_BUTTON_EXAMPLES = [
  'اطلاعات بیشتر',
  'مشاهده محصولات',
  'ثبت سفارش',
];

const AUTO_RESPONSE_GUIDE_CONTENT = (
  <>
    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      بخش دستورات در تنظیمات آیدا به کاربران این امکان را می‌دهد که برای پیام‌ها، سوال‌ها یا کلمات مشخص،
      پاسخ‌های ثابت و از پیش تعریف‌شده تنظیم کنند.
    </Typography>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      این قابلیت زمانی کاربرد دارد که بخواهید آیدا در بعضی شرایط، به‌جای تولید پاسخ خلاقانه، همیشه یک پاسخ
      مشخص و ثابت برای مشتری ارسال کند. برای مثال می‌توانید برای سوال‌هایی مثل «آدرس فروشگاه»، «شماره تماس»،
      «قیمت محصول» یا «ساعت کاری» پاسخ‌های آماده تعریف کنید.
    </Typography>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      اگر قبلاً دستوراتی ایجاد کرده باشید، همه آن‌ها در جدول دستورات نمایش داده می‌شوند. در این جدول
      می‌توانید لیست کامل دستورات ثبت‌شده را مشاهده و مدیریت کنید.
    </Typography>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      اگر هنوز دستوری ایجاد نکرده‌اید، می‌توانید با استفاده از دکمه افزودن
      <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', mx: 0.75 , backgroundColor: 'primary.main' , borderRadius: '50%' , padding: '4px' }}>
        <AddIcon fontSize="small" sx={{ color: 'white' }} />
      </Box>
      یک دستور جدید بسازید.
    </Typography>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      پس از کلیک روی دکمه افزودن، فرم ساخت دستور به همراه پیش‌نمایش چت‌باکس برای شما نمایش داده می‌شود تا
      بتوانید هم‌زمان نتیجه نهایی پیام را مشاهده کنید.
    </Typography>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      فرم ایجاد دستور شامل بخشی برای تعیین نوع شرط پاسخ است. در این قسمت مشخص می‌کنید که پاسخ ثابت چه زمانی
      برای مشتری ارسال شود.
    </Typography>

    <Box component="ul" sx={{ m: 0, mb: 2, pr: 3, pl: 0, lineHeight: 1.9 }}>
      <Box component="li">
        <Typography variant="body1">حالت «برابر با»</Typography>
      </Box>
      <Box component="li">
        <Typography variant="body1">حالت «شامل»</Typography>
      </Box>
    </Box>

    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
      حالت «برابر با»
    </Typography>
    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.9, textAlign: 'justify' }}>
      در این حالت، مشتری باید دقیقاً همان متنی را ارسال کند که شما در فرم وارد کرده‌اید تا پاسخ ثابت برای او
      نمایش داده شود.
    </Typography>
    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.9 }}>
      برای مثال اگر مقدار شرط برابر با عبارت زیر باشد:
    </Typography>
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: '12px', backgroundColor: '#fff' }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        "قیمت محصول"
      </Typography>
    </Paper>
    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      فقط زمانی پاسخ ثابت ارسال می‌شود که پیام مشتری دقیقاً همین عبارت باشد.
    </Typography>

    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
      حالت «شامل»
    </Typography>
    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.9, textAlign: 'justify' }}>
      در این حالت، کافی است متن یا کلمه مشخص‌شده در بخشی از پیام مشتری وجود داشته باشد تا پاسخ ثابت ارسال شود.
    </Typography>
    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.9 }}>
      برای مثال اگر مقدار شرط کلمه زیر باشد:
    </Typography>
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: '12px', backgroundColor: '#fff' }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        "قیمت"
      </Typography>
    </Paper>
    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.9, textAlign: 'justify' }}>
      تمام پیام‌هایی که شامل این کلمه باشند، پاسخ تعیین‌شده را دریافت خواهند کرد؛ حتی اگر متن کامل پیام
      متفاوت باشد.
    </Typography>

    <Box component="ul" sx={{ m: 0, mb: 2, pr: 3, pl: 0, lineHeight: 1.9 }}>
      {AUTO_RESPONSE_CONTAINS_EXAMPLES.map((example) => (
        <Box component="li" key={example}>
          <Typography variant="body1">{example}</Typography>
        </Box>
      ))}
    </Box>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      همه این پیام‌ها می‌توانند پاسخ ثابت تعریف‌شده را دریافت کنند.
    </Typography>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      در انتهای فرم، گزینه‌ای با عنوان «دکمه در پاسخ آیدا» وجود دارد. با فعال کردن این گزینه، می‌توانید برای
      پاسخ آیدا دکمه‌های قابل کلیک تعریف کنید.
    </Typography>

    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.9, textAlign: 'justify' }}>
      این دکمه‌ها معمولاً برای هدایت کاربران به صفحات دیگر استفاده می‌شوند. برای مثال می‌توانید دکمه‌ای با
      عنوان‌های زیر ایجاد کنید و آن را به لینک موردنظر متصل نمایید:
    </Typography>

    <Box component="ul" sx={{ m: 0, mb: 2, pr: 3, pl: 0, lineHeight: 1.9 }}>
      {AUTO_RESPONSE_BUTTON_EXAMPLES.map((label) => (
        <Box component="li" key={label}>
          <Typography variant="body1">{label}</Typography>
        </Box>
      ))}
    </Box>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9 }}>
      در هر پاسخ حداکثر می‌توان 3 دکمه اضافه کرد.
    </Typography>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      در تمام مراحل ساخت یا ویرایش دستور، بخش پیش‌نمایش چت‌باکس نمایش داده می‌شود تا کاربر بتواند ظاهر نهایی
      پیام و دکمه‌ها را قبل از ثبت مشاهده کند.
    </Typography>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      پس از تکمیل فرم، با کلیک روی دکمه «ایجاد»، دستور جدید ذخیره شده و به لیست دستورات اضافه می‌شود.
    </Typography>

    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.9, textAlign: 'justify' }}>
      هر دستور در جدول دستورات دارای گزینه‌های مدیریتی مختلف است، از جمله:
    </Typography>

    <Box component="ul" sx={{ m: 0, mb: 2, pr: 3, pl: 0, lineHeight: 1.9 }}>
      <Box component="li">
        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ویرایش
          <EditIcon fontSize="small" sx={{ color: 'primary.main' }} />
        </Typography>
      </Box>
      <Box component="li">
        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          حذف
          <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
        </Typography>
      </Box>
    </Box>

    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9, textAlign: 'justify' }}>
      با استفاده از دکمه ویرایش، کاربر می‌تواند تمام بخش‌های دستور را تغییر دهد یا بروزرسانی کند. دکمه حذف
      نیز برای پاک کردن دستورهایی استفاده می‌شود که دیگر موردنیاز نیستند.
    </Typography>

    <Typography variant="body1" sx={{ lineHeight: 1.9, textAlign: 'justify' }}>
      در کنار دکمه افزودن، گزینه‌ای با عنوان «پاک کردن همه دستورات» نیز وجود دارد. با استفاده از این گزینه،
      تمام دستورهای ثبت‌شده حذف می‌شوند و پاسخ‌های ثابت تعریف‌شده از سیستم پاک خواهند شد.
    </Typography>
  </>
);

const autoResponseTableSx = {
  borderCollapse: 'separate',
  borderSpacing: '5px 0',
  '& .MuiTableCell-root': {
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
};

const RESPONSE_MESSAGE_PREVIEW_LENGTH = 40;

const truncateResponseMessage = (message: string) => {
  if (!message || message.length <= RESPONSE_MESSAGE_PREVIEW_LENGTH) return message;
  return `${message.slice(0, RESPONSE_MESSAGE_PREVIEW_LENGTH)}...`;
};

export const AutomateResponseContent: FC = () => {
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  // Auto-response list states
  const [autoResponses, setAutoResponses] = useState<AutoResponseEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Add auto-response states
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingResponse, setAddingResponse] = useState(false);

  // Edit auto-response states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AutoResponseEntry | null>(null);
  const [updatingResponse, setUpdatingResponse] = useState(false);
  
  // Edit form states (separate from Add form)
  const [editDirectChecked, setEditDirectChecked] = useState(true);
  const [editCommentChecked, setEditCommentChecked] = useState(false);
  const [editConditionType, setEditConditionType] = useState('equals');
  const [editTriggerValue, setEditTriggerValue] = useState('');
  const [editResponseMessage, setEditResponseMessage] = useState('');
  const [editHasButtonInResponse, setEditHasButtonInResponse] = useState(false);
  const [editButtons, setEditButtons] = useState<Array<{text: string, url: string}>>([{text: '', url: ''}]);

  // OrderBot form states (for Add)
  const [directChecked, setDirectChecked] = useState(true);
  const [commentChecked, setCommentChecked] = useState(false);
  const [conditionType, setConditionType] = useState('equals');
  const [triggerValue, setTriggerValue] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [hasButtonInResponse, setHasButtonInResponse] = useState(false);
  const [buttons, setButtons] = useState<Array<{text: string, url: string}>>([{text: '', url: ''}]);

  // Flush responses states
  const [showFlushDialog, setShowFlushDialog] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);

  const { showError } = useError();
  const { t, currentLanguage } = useTranslation();

  const formatDate = (date: string | null) => {
    if (!date) return '---';
    const formatted = formatJalali(new Date(date), 'HH:mm yyyy/MM/dd');
    return currentLanguage === 'fa' ? toPersianDigits(formatted) : formatted;
  };

  let flag = false;
  ////////////////////////////////  ////////////////////////////////////////////////////////////////////////////////////////////
  const fetchAutoResponses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/auto-response/list');
      setAutoResponses(response.data.items || response.data || []);
    } catch (err: any) {
      if (!flag) {
        enqueueSnackbar(t('automation.automation_bot.error.sessionExpired'), { 
          variant: 'error',
          autoHideDuration: 5000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
        flag = true;
      }
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/auth/auth';
      }, 4000);
    } finally {
      setLoading(false);
    }
  };
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  // Register tour function with global tour context
  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  useEffect(() => {
    fetchAutoResponses();
    // eslint-disable-next-line
  }, []);

  const handleDelete = async (id: number) => {
    setDeleting(true);
    setSuccess(null);
    try {
      await api.delete(`/auto-response/${id}`);
      enqueueSnackbar(t('automation.automation_bot.success.delete'), { 
        variant: 'success',
        autoHideDuration: 5000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      setAutoResponses((prev) => prev.filter((k) => k.id !== id));
    } catch (err: any) {
      enqueueSnackbar(t('automation.automation_bot.error.deleteError'), { 
        variant: 'error',
        autoHideDuration: 5000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      showError(err);
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to get message type display text
  // const getMessageTypeDisplay = (type: string) => {
  //   switch (type) {
  //     case 'direct':
  //       return t('automation.table.direct');
  //     case 'comment':
  //       return t('automation.table.comment');
  //     case 'both':
  //       return t('automation.table.both');
  //     default:
  //       return type;
  //   }
  // };

  // Helper function to get condition type display text
  const getConditionTypeDisplay = (type: string) => {
    switch (type) {
      case 'equals':
        return t('automation.table.equals');
      case 'contains':
        return t('automation.table.contains');
      default:
        return type;
    }
  };

  // Open edit modal and populate fields
  const handleEditClick = (entry: AutoResponseEntry) => {
    setEditingEntry(entry);
    
    // Populate EDIT form fields with entry data (separate from Add form)
    setEditDirectChecked(entry.message_type === 'direct' || entry.message_type === 'both');
    setEditCommentChecked(entry.message_type === 'comment' || entry.message_type === 'both');
    setEditConditionType(entry.condition_type);
    setEditTriggerValue(entry.trigger_value);
    
    // Parse response message to check if it contains markdown links
    const responseMsg = entry.response_message || '';
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const matches = [...responseMsg.matchAll(markdownLinkRegex)];
    
    if (matches.length > 0) {
      // Extract message without links
      let messageWithoutLinks = responseMsg;
      matches.reverse().forEach((match) => {
        messageWithoutLinks = messageWithoutLinks.replace(match[0], '');
      });
      matches.reverse();
      setEditResponseMessage(messageWithoutLinks.trim());
      setEditHasButtonInResponse(true);
      // Extract all buttons
      const extractedButtons = matches.map(match => ({
        text: match[1],
        url: match[2]
      }));
      setEditButtons(extractedButtons);
    } else {
      setEditResponseMessage(responseMsg);
      setEditHasButtonInResponse(false);
      setEditButtons([{text: '', url: ''}]);
    }
    
    setShowEditModal(true);
  };

  // Update auto-response function
  const handleUpdateCommand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEntry) return;
    
    setUpdatingResponse(true);

    try {
      // تعیین نوع کانال بر اساس checkbox های انتخاب شده (از Edit state ها)
      let type = '';
      if (editDirectChecked && editCommentChecked) {
        type = 'both';
      } else if (editDirectChecked) {
        type = 'direct';
      } else if (editCommentChecked) {
        type = 'comment';
      } else {
        enqueueSnackbar(t('automation.automation_bot.error.pick_channel'), { 
          variant: 'error',
          autoHideDuration: 4000,
        });
        return;
      }

      if (!editTriggerValue.trim()) {
        enqueueSnackbar(t('automation.automation_bot.error.enter_trigger'), { 
          variant: 'error',
          autoHideDuration: 4000,
        });
        return;
      }

      if (!editResponseMessage.trim()) {
        enqueueSnackbar(t('automation.automation_bot.error.enter_response'), { 
          variant: 'error',
          autoHideDuration: 4000,
        });
        return;
      }

      // ساخت response message نهایی برای Edit
      let finalEditResponseMessage = editResponseMessage.trim();
      
      // اگر switch دکمه فعال باشد، لینک‌های همه دکمه‌ها را اضافه کن
      if (editHasButtonInResponse && editButtons.length > 0) {
        editButtons.forEach((button) => {
          if (button.text.trim() && button.url.trim()) {
            finalEditResponseMessage += `[${button.text.trim()}](${button.url.trim()})`;
          }
        });
      }
      
      const updateData = {
        message_type: type,
        condition_type: editConditionType,
        trigger_value: convertPersianToEnglishNumbers(editTriggerValue.trim()),
        response_message: convertPersianToEnglishNumbers(finalEditResponseMessage)
      };

      await api.put(`/auto-response/${editingEntry.id}`, updateData);

      enqueueSnackbar(t('automation.automation_bot.success.update'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          marginTop: '20px',
        },
      });
      
      // Reset EDIT form and close modal
      setEditDirectChecked(false);
      setEditCommentChecked(false);
      setEditConditionType('equals');
      setEditTriggerValue('');
      setEditResponseMessage('');
      setEditHasButtonInResponse(false);
      setEditButtons([{text: '', url: ''}]);
      setShowEditModal(false);
      setEditingEntry(null);
      
      // Refresh the list
      fetchAutoResponses();
    } catch (error) {
      showError(error);
    } finally {
      setUpdatingResponse(false);
    }
  };

  // Reset Add form to initial state
  const resetAddForm = () => {
    setDirectChecked(true);
    setCommentChecked(false);
    setConditionType('equals');
    setTriggerValue('');
    setResponseMessage('');
    setHasButtonInResponse(false);
    setButtons([{text: '', url: ''}]);
  };

/////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Add OrderBot command function
  const handleAddCommand = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingResponse(true);

    try {
      // تعیین نوع کانال بر اساس checkbox های انتخاب شده
      let type = '';
      if (directChecked && commentChecked) {
        type = 'both';
      } else if (directChecked) {
        type = 'direct';
      } else if (commentChecked) {
        type = 'comment';
      } else {
        // اگر هیچ کدام انتخاب نشده، خطا نمایش دهید
        enqueueSnackbar(t('automation.automation_bot.error.pick_channel'), { 
          variant: 'error',
          autoHideDuration: 4000,
        });
        return;
      }

      // بررسی اینکه آیا مقادیر ضروری وارد شده‌اند
      if (!triggerValue.trim()) {
        enqueueSnackbar(t('automation.automation_bot.error.enter_trigger'), { 
          variant: 'error',
          autoHideDuration: 4000,
        });
        return;
      }

      if (!responseMessage.trim()) {
        enqueueSnackbar(t('automation.automation_bot.error.enter_response'), { 
          variant: 'error',
          autoHideDuration: 4000,
        });
        return;
      }

      // ساخت داده‌های OrderBot
      let finalResponseMessage = responseMessage.trim();
      
      // اگر switch دکمه فعال باشد، لینک‌های همه دکمه‌ها را اضافه کن
      if (hasButtonInResponse && buttons.length > 0) {
        buttons.forEach((button) => {
          if (button.text.trim() && button.url.trim()) {
            finalResponseMessage += `[${button.text.trim()}](${button.url.trim()})`;
          }
        });
      }
      
      const orderBotData = {
        message_type: type,
        condition_type: conditionType,
        trigger_value: convertPersianToEnglishNumbers(triggerValue.trim()),
        response_message: convertPersianToEnglishNumbers(finalResponseMessage)
      };

      // ارسال به API
      await api.post('/auto-response/add', orderBotData);

      enqueueSnackbar(t('automation.automation_bot.add.success'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          marginTop: '20px',
        },
      });
      
      // Reset form
      resetAddForm();
      setShowAddForm(false);
      
      // Refresh the list
      fetchAutoResponses();
    } catch (error) {
      showError(error);
    } finally {
      setAddingResponse(false);
    }
  };
/////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Flush responses functions
  const handleFlush = async () => {
    setFlushing(true);

    try {
      await api.delete('/auto-response/flush');
      enqueueSnackbar(t('automation.automation_bot.flush.success'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          marginTop: '20px',
        },
      });
      setShowFlushDialog(false);
      // Refresh the responses list after flushing  
      fetchAutoResponses();
    } catch (error) {
      showError(error);
    } finally {
      setFlushing(false);
    }
  };

  // Validation for Add form
  const isAddFormValid = (directChecked || commentChecked) && 
                        triggerValue.trim() !== '' && 
                        responseMessage.trim() !== '' &&
                        (!hasButtonInResponse || buttons.every(btn => btn.text.trim() !== '' && btn.url.trim() !== ''));

  // Validation for Edit form
  const isEditFormValid = (editDirectChecked || editCommentChecked) && 
                          editTriggerValue.trim() !== '' && 
                          editResponseMessage.trim() !== '' &&
                          (!editHasButtonInResponse || editButtons.every(btn => btn.text.trim() !== '' && btn.url.trim() !== ''));

  // Check if Edit form has changes
  const hasEditChanges = editingEntry ? (
    (editDirectChecked !== (editingEntry.message_type === 'direct' || editingEntry.message_type === 'both')) ||
    (editCommentChecked !== (editingEntry.message_type === 'comment' || editingEntry.message_type === 'both')) ||
    (editConditionType !== editingEntry.condition_type) ||
    (editTriggerValue !== editingEntry.trigger_value) ||
    (editResponseMessage !== editingEntry.response_message)
  ) : false;

  const isEditButtonEnabled = isEditFormValid && hasEditChanges;

  const handleToggleActive = async (id: number) => {
    try {
      await api.patch(`/auto-response/${id}/toggle`);
      
      // Update local state immediately for better UX
      setAutoResponses((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, is_active: !entry.is_active } : entry
        )
      );
      
      enqueueSnackbar(t('automation.automation_bot.success.toggleActive'), { 
        variant: 'success',
        autoHideDuration: 2000,
      });
      
      // Refresh list to ensure sync with server
      fetchAutoResponses();
    } catch (error) {
      enqueueSnackbar(t('automation.automation_bot.error.toggleActive'), { 
        variant: 'error',
        autoHideDuration: 2000,
      });
    }
  };

  return (
    <Box sx={{marginTop: '20px', px: 3}}>
      {/* Title and Buttons */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          <Typography variant="h5" className="first-step" fontSize={{ xs: '1rem', sm: '1.25rem' }} noWrap>
            {t('automation.automation_bot.list.title')}
          </Typography>
          <IconButton
            aria-label="راهنمای دستورات"
            onClick={() => setShowGuideModal(true)}
            sx={{
              color: 'primary.main',
              width: 32,
              height: 32,
              flexShrink: 0,
            }}
          >
            <HelpOutlineIcon fontSize="medium" />
          </IconButton>
        </Box>
        <Box display="flex" gap={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button
            className="second-step"
            variant="contained"
            aria-label={showAddForm ? t('automation.automation_bot.add.cancel') : t('automation.automation_bot.add.title')}
            onClick={() => {
              if (showAddForm) {
                resetAddForm();
              }
              setShowAddForm(!showAddForm);
            }}
            color={showAddForm ? 'error' : 'primary'}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 },
              py: { xs: 0.625, sm: 1 },
              minWidth: { xs: 38, sm: 'auto' },
              minHeight: { xs: 38, sm: 36 },
              '& .MuiButton-startIcon': {
                display: { xs: 'none', sm: 'inherit' },
                mr: { sm: 1 },
                '& > *:nth-of-type(1)': {
                  fontSize: { sm: 20 },
                },
              },
            }}
            startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {showAddForm ? t('automation.automation_bot.add.cancel') : t('automation.automation_bot.add.title')}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline-flex', sm: 'none' }, alignItems: 'center' }}>
              {showAddForm ? <CloseIcon sx={{ fontSize: 20 }} /> : <AddIcon sx={{ fontSize: 20 }} />}
            </Box>
          </Button>
          <Button
            className="third-step"
            variant="contained"
            color="error"
            aria-label={t('automation.automation_bot.flush.deleteAll')}
            onClick={() => setShowFlushDialog(true)}
            disabled={flushing}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 },
              py: { xs: 0.625, sm: 1 },
              minWidth: { xs: 38, sm: 'auto' },
              minHeight: { xs: 38, sm: 36 },
              '& .MuiButton-startIcon': {
                display: { xs: 'none', sm: 'inherit' },
                mr: { sm: 1 },
              },
            }}
            startIcon={<DeleteSweepIcon />}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {t('automation.automation_bot.flush.deleteAll')}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline-flex', sm: 'none' }, alignItems: 'center' }}>
              <DeleteSweepIcon sx={{ fontSize: 20 }} />
            </Box>
          </Button>
        </Box>
      </Box>

      <Dialog
        open={showGuideModal}
        onClose={() => setShowGuideModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          راهنمای دستورات
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 , px:5}}>
          {AUTO_RESPONSE_GUIDE_CONTENT}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setShowGuideModal(false)} variant="contained">
            بستن
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add auto-response Form */}
      {showAddForm && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' , textAlign: 'center' , mb: 2 , fontSize: {xs: '1rem', md: '1.2rem'}}}>
            {t('automation.automation_bot.add.title')}
          </Typography>
          <Box sx={{ display: 'flex' , gap: { xs: 2, sm: 3, md: 5 }, flexDirection: {xs: 'column', sm: 'column', md: 'row'}}}>
            <Box sx={{ width: {xs: '100%', sm: '100%', md: '50%'}}}>
              <form onSubmit={handleAddCommand}>
              {/* Conditional Trigger Section */}
              <Box sx={{ mb: 3 }}>
              {/* <Typography variant="body1" sx={{ mb: 1 , fontWeight: 'bold'}}>
                {t('automation.automation_bot.add.if_user_in')}
              </Typography> */}
              
                {/* <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}> */}
                    {/* <Button
                      variant="outlined"
                      onClick={() => setDirectChecked(!directChecked)}
                      sx={{
                        flex: 1,
                        maxWidth: '200px',
                        border: directChecked ? '2px solid #4caf50' : '2px solid rgba(0, 0, 0, 0.23)',
                        boxShadow: directChecked ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                        backgroundColor: directChecked ? '#4caf50' : 'transparent',
                        color: directChecked ? 'white' : 'inherit',
                        '&:hover': {
                          backgroundColor: directChecked ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                          border: '2px solid #4caf50',
                          boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
                        }
                      }}
                    >
                      {t('automation.table.direct')}
                    </Button> */}
                    {/* <Button
                      variant="outlined"
                      onClick={() => setCommentChecked(!commentChecked)}
                      sx={{
                        flex: 1,
                        maxWidth: '200px',
                        border: commentChecked ? '2px solid #4caf50' : '2px solid rgba(0, 0, 0, 0.23)',
                        boxShadow: commentChecked ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                        backgroundColor: commentChecked ? '#4caf50' : 'transparent',
                        color: commentChecked ? 'white' : 'inherit',
                        '&:hover': {
                          backgroundColor: commentChecked ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                          border: '2px solid #4caf50',
                          boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
                        }
                      }}
                    >
                      {t('automation.table.comment')}
                    </Button> */}
                  {/* </Box>
                </Box> */}

              <Typography variant="body1" sx={{ mb: 2 , fontWeight: 'bold' , fontSize: {xs: '0.9rem', md: '1.2rem'}}}>
                {t('automation.automation_bot.add.trigger')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t('automation.automation_bot.add.condition')}</InputLabel>
                  <Select 
                    label={t('automation.automation_bot.add.condition')}
                    value={conditionType}
                    onChange={(e) => setConditionType(e.target.value)}
                  >
                    <MenuItem value="equals">{t('automation.table.equals')}</MenuItem>
                    <MenuItem value="contains">{t('automation.table.contains')}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={t('automation.automation_bot.add.value')}
                  variant="outlined"
                  size="small"
                  value={triggerValue}
                  onChange={(e) => setTriggerValue(e.target.value)}
                  sx={{ width: '100%' }}
                />
              </Box>

              <Typography variant="body1" sx={{ mb: 2 , fontWeight: 'bold' , fontSize: {xs: '0.9rem', md: '1.2rem'}}}>
                {t('automation.automation_bot.add.send_message')}
              </Typography>
              </Box>

              {/* Message Selection Section */}
              <Box sx={{ mb: 3 }}>
              <TextField
                label={t('automation.automation_bot.add.response_message')}
                variant="outlined"
                multiline
                rows={3}
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                sx={{ width: '100%' }}
              />
              </Box>

              {/* Switch for having button in response message */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2, mb: 2}}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Switch
                    checked={hasButtonInResponse}
                    onChange={(e) => {
                      setHasButtonInResponse(e.target.checked);
                      if (!e.target.checked) {
                        setButtons([{text: '', url: ''}]);
                      }
                    }}
                  />
                  <Typography sx={{fontWeight: 'bold' , fontSize: {xs: '0.9rem', md: '1.2rem'}}}>{t('automation.automation_bot.add.has_button_in_response') || 'داشتن دکمه در جواب آیدا'}</Typography>
                </Box>
                {hasButtonInResponse && (
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <IconButton
                      onClick={() => {
                        if (buttons.length < 3) {
                          setButtons([...buttons, {text: '', url: ''}]);
                        }
                      }}
                      disabled={buttons.length >= 3}
                      sx={{
                        width: '35px',
                        height: '35px',
                        backgroundColor: 'success.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'success.dark' },
                        '&.Mui-disabled': { backgroundColor: 'grey.300' }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        if (buttons.length > 1) {
                          setButtons(buttons.slice(0, -1));
                        }
                      }}
                      disabled={buttons.length <= 1}
                      sx={{
                        width: '35px',
                        height: '35px',
                        backgroundColor: 'error.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'error.dark' },
                        '&.Mui-disabled': { backgroundColor: 'grey.300' }
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {/* Input for button text when switch is enabled */}
              {hasButtonInResponse && (
                <Box sx={{ mb: 2 }}>
                  {buttons.map((button, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <TextField
                        label={`${t('automation.automation_bot.add.button_text') || 'متن دکمه'} ${index + 1}`}
                        variant="outlined"
                        value={button.text}
                        onChange={(e) => {
                          const newButtons = [...buttons];
                          newButtons[index].text = e.target.value;
                          setButtons(newButtons);
                        }}
                        sx={{ width: '100%', mb: 2 }}
                      />
                      <TextField
                        label={`${t('automation.automation_bot.add.button_url') || 'آدرس دکمه'} ${index + 1}`}
                        variant="outlined"
                        value={button.url}
                        onChange={(e) => {
                          const newButtons = [...buttons];
                          newButtons[index].url = e.target.value;
                          setButtons(newButtons);
                        }}
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {/* Action Button */}
                <Box display="flex" justifyContent={currentLanguage === 'fa' ? 'flex-end' : 'flex-start'} gap={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={addingResponse || !isAddFormValid}
                  sx={{backgroundColor: 'success.main' , flex: 1 , maxWidth: '200px',
                    '&:hover': {
                          backgroundColor: 'success.main',
                        }
                  }}
                >
                  {addingResponse ? <CircularProgress size={24} /> : t('automation.automation_bot.add.create')}
                </Button>
              <Button
                variant="contained"
                onClick={() => {
                  resetAddForm();
                  setShowAddForm(false);
                }}
                sx={{backgroundColor: 'error.main' , color: 'white' , flex: 1 , maxWidth: '200px',
                  '&:hover': {
                        backgroundColor: 'error.main',
                      }
                }}
              >
                {t('common.cancel')}
              </Button>
              </Box>
              </form>
            </Box>
            <Box sx={{ width: {xs: '100%', sm: '100%', md: '50%'}, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              <Box sx={{
                width: {xs: '100%', sm: '100%', md: '75%'} , 
                height: '100%' , 
                minHeight: {xs: '200px', sm: '250px', md: '300px'}, 
                minWidth: {xs: '100%', sm: '100%', md: '300px'} , 
                maxWidth: {xs: '100%', sm: '100%', md: 'none'},
                borderRadius: '20px', 
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)', 
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: {xs: 1, sm: 1.25, md: 1.5}, backgroundColor: '#009688'}}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' , textAlign: 'center', mb: 0, color: 'white', fontSize: {xs: '0.9rem', sm: '1rem', md: '1.25rem'}}}>
                    {t('automation.automation_bot.preview.title')}
                  </Typography>
                </Box>
                <Box sx={{mt: {xs: 2, sm: 3, md: 4}, ml: {xs: 1, sm: 1.5, md: 2}, pr: {xs: 2, sm: 3, md: 5}}}>
                  <Box sx={{p: {xs: 1, sm: 1.25, md: 1.5}, backgroundColor: '#009688', width: 'max-content', color: 'white', borderRadius: '10px 10px 10px 0', maxWidth: {xs: '85%', sm: '85%', md: '80%'}, fontSize: {xs: '0.85rem', sm: '0.9rem', md: '1rem'}, wordBreak: 'break-word', overflowWrap: 'break-word', overflow: 'hidden'}}>
                    {conditionType === 'contains' ? `...${triggerValue || ''}...` : (triggerValue || '')}
                  </Box>
                </Box>
                <Box sx={{mt: {xs: 1, sm: 1.5, md: 2}, mr: {xs: 1, sm: 2, md: 3}, display: 'flex', justifyContent: 'flex-end', alignItems: 'end', pl: {xs: 2, sm: 4, md: 6}, pb: {xs: 2, sm: 3, md: 4}}}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2, p: {xs: 1, sm: 1.25, md: 1.5}, backgroundColor: '#f5f5f5', width: 'max-content', color: 'black', borderRadius: '10px 10px 0px 10px', maxWidth: {xs: '75%', sm: '85%', md: '90%'}, fontSize: {xs: '0.85rem', sm: '0.9rem', md: '1rem'}, wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'break-spaces', overflow: 'hidden'}}>
                    <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', width: '100%', alignSelf: 'stretch' }}>
                      {responseMessage || ''}
                    </Box>
                    {hasButtonInResponse && buttons.map((button, index) => (
                      button.text.trim() && button.url.trim() && (
                        <Button key={index} href={button.url} target="_blank" sx={{width: '100%', color:'black', border:'1px solid black'}}>
                          {button.text}
                        </Button>
                      )
                    ))}
                  </Box>
                  <img src="/images/main.png" alt="" style={{width: 'clamp(24px, 5vw, 34px)', height: 'clamp(30px, 6vw, 42px)', marginRight: '10px', marginTop: '8px'}} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Auto-response List */}
      <Paper sx={{ p: { xs: 1.5, md: 3 }, overflowX: 'auto' }} className="fourth-step">
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer>
            <Table sx={autoResponseTableSx}>
              <TableHead>
                <TableRow>
                  {/* <TableCell sx={{textAlign: 'center'}}>{t('automation.table.channelType')}</TableCell> */}
                  <TableCell sx={{textAlign: 'center'}}>{t('automation.table.conditionType')}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>{t('automation.table.triggerValue')}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>{t('automation.table.responseMessage')}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>{t('automation.table.createdAt')}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>{t('automation.table.operations')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {autoResponses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('automation.automation_bot.list.empty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  autoResponses
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((entry) => (
                      <TableRow key={entry.id}>
                        {/* <TableCell sx={{textAlign:'center'}}>
                          <Chip 
                            label={getMessageTypeDisplay(entry.message_type)}
                            color={'default'}
                            size="small"
                          />
                        </TableCell> */}
                        <TableCell sx={{textAlign:'center'}}>
                          <Chip
                            label={getConditionTypeDisplay(entry.condition_type)}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{textAlign:'center'}}>{entry.trigger_value}</TableCell>
                        <TableCell sx={{textAlign:'center'}}>{truncateResponseMessage(entry.response_message)}</TableCell>
                        <TableCell sx={{textAlign:'center'}}>
                          {formatDate(entry.created_at)}
                        </TableCell>  
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'nowrap' }}>
                            <Chip
                              label={entry.is_active ? t('automation.automation_bot.add.active') : t('automation.automation_bot.add.inactive')}
                              color={entry.is_active ? 'success' : 'default'}
                              size="small"
                            />
                            <Switch
                              checked={entry.is_active}
                              onChange={() => handleToggleActive(entry.id)}
                              color="primary"
                            />
                            <IconButton
                              color="primary"
                              onClick={() => handleEditClick(entry)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => setDeleteId(entry.id)}
                              disabled={deleting}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      </Paper>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[{ value: 5, label: formatPersianNumber(5) }, { value: 10, label: formatPersianNumber(10) }, { value: 25, label: formatPersianNumber(25) }]}
        component="div"
        count={autoResponses.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t('common.rowsPerPage')}
        labelDisplayedRows={({ from, to }) => `${formatPersianNumber(from)} - ${formatPersianNumber(to)}`}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>{t('automation.automation_bot.list.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('automation.automation_bot.list.confirmDelete')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>{t('common.cancel')}</Button>
          <Button
            onClick={() => deleteId && handleDelete(deleteId)}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : t('automation.automation_bot.list.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog 
        open={showEditModal} 
        onClose={() => {
          setShowEditModal(false);
          setEditingEntry(null);
          // Reset EDIT form
          setEditDirectChecked(true);
          setEditCommentChecked(false);
          setEditConditionType('equals');
          setEditTriggerValue('');
          setEditResponseMessage('');
          setEditHasButtonInResponse(false);
          setEditButtons([{text: '', url: ''}]);
        }}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '900px',
            padding: '10px',
          },
        }}
        fullWidth
      >
        <DialogTitle sx={{ fontSize: {xs: '1.1rem', md: '1.2rem'}, fontWeight: 'bold' , textAlign: 'center' , pb: 0, mb:2 }}>{t('automation.automation_bot.edit.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3, md: 5 }, flexDirection: {xs: 'column', sm: 'column', md: 'row'}}}>
            <Box sx={{ width: {xs: '100%', sm: '100%', md: '50%'}}}>
              <form onSubmit={handleUpdateCommand}>
                {/* Conditional Trigger Section */}
                <Box sx={{ mb: 3, mt: 2}}>
                  {/* <Typography variant="body1" sx={{ mb: 2 , fontWeight: 'bold'}}>
                    {t('automation.automation_bot.add.if_user_in')}
                  </Typography> */}
                  
                  {/* <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}> */}
                      {/* <Button
                        variant="outlined"
                        onClick={() => setEditDirectChecked(!editDirectChecked)}
                        sx={{
                          flex: 1,
                          border: editDirectChecked ? '2px solid #4caf50' : '2px solid rgba(0, 0, 0, 0.23)',
                          boxShadow: editDirectChecked ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                          backgroundColor: editDirectChecked ? '#4caf50' : 'transparent',
                          color: editDirectChecked ? 'white' : 'inherit',
                          '&:hover': {
                            backgroundColor: editDirectChecked ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                            border: '2px solid #4caf50',
                            boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
                          }
                        }}
                      >
                        {t('automation.table.direct')}
                      </Button> */}
                      {/* <Button
                        variant="outlined"
                        onClick={() => setEditCommentChecked(!editCommentChecked)}
                        sx={{
                          flex: 1,
                          border: editCommentChecked ? '2px solid #4caf50' : '2px solid rgba(0, 0, 0, 0.23)',
                          boxShadow: editCommentChecked ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                          backgroundColor: editCommentChecked ? '#4caf50' : 'transparent',
                          color: editCommentChecked ? 'white' : 'inherit',
                          '&:hover': {
                            backgroundColor: editCommentChecked ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                            border: '2px solid #4caf50',
                            boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
                          }
                        }}
                      >
                        {t('automation.table.comment')}
                      </Button> */}
                    {/* </Box>
                  </Box> */}

                  <Typography variant="body1" sx={{ mb: 2 , fontWeight: 'bold' , fontSize: {xs: '0.9rem', md: '1.2rem'}}}>
                    {t('automation.automation_bot.add.trigger')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>{t('automation.automation_bot.add.condition')}</InputLabel>
                      <Select 
                        label={t('automation.automation_bot.add.condition')}
                        value={editConditionType}
                        onChange={(e) => setEditConditionType(e.target.value)}
                      >
                        <MenuItem value="equals">{t('automation.table.equals')}</MenuItem>
                        <MenuItem value="contains">{t('automation.table.contains')}</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label={t('automation.automation_bot.add.value')}
                      variant="outlined"
                      size="small"
                      value={editTriggerValue}
                      onChange={(e) => setEditTriggerValue(e.target.value)}
                      sx={{ width: '100%'}}
                    />
                  </Box>

                  <Typography variant="body1" sx={{ mb: 2 , fontWeight: 'bold' , fontSize: {xs: '0.9rem', md: '1.2rem'}}}>
                    {t('automation.automation_bot.add.send_message')}
                  </Typography>
                </Box>

                {/* Message Selection Section */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    label={t('automation.automation_bot.add.response_message')}
                    variant="outlined"
                    multiline
                    rows={3}
                    value={editResponseMessage}
                    onChange={(e) => setEditResponseMessage(e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </Box>
                {/* Switch for having button in response message */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                      <Switch
                        checked={editHasButtonInResponse}
                        onChange={(e) => {
                          setEditHasButtonInResponse(e.target.checked);
                          if (!e.target.checked) {
                            setEditButtons([{text: '', url: ''}]);
                          }
                        }}
                      />
                      <Typography sx={{fontWeight: 'bold', whiteSpace: 'nowrap', fontSize: {xs: '0.9rem', md: '1.2rem'}}}>{t('automation.automation_bot.add.has_button_in_response') || 'داشتن دکمه در جواب آیدا'}</Typography>
                    </Box>
                    {editHasButtonInResponse && (
                      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <IconButton
                          onClick={() => {
                            if (editButtons.length < 3) {
                              setEditButtons([...editButtons, {text: '', url: ''}]);
                            }
                          }}
                          disabled={editButtons.length >= 3}
                          sx={{
                            width: '35px',
                            height: '35px',
                            backgroundColor: 'success.main',
                            color: 'white',
                            '&:hover': { backgroundColor: 'success.dark' },
                            '&.Mui-disabled': { backgroundColor: 'grey.300' }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            if (editButtons.length > 1) {
                              setEditButtons(editButtons.slice(0, -1));
                            }
                          }}
                          disabled={editButtons.length <= 1}
                          sx={{
                            width: '35px',
                            height: '35px',
                            backgroundColor: 'error.main',
                            color: 'white',
                            '&:hover': { backgroundColor: 'error.dark' },
                            '&.Mui-disabled': { backgroundColor: 'grey.300' }
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                        
                  {/* Input for button text when switch is enabled */}
                  {editHasButtonInResponse && (
                    <Box sx={{ mb: 2 }}>
                      {editButtons.map((button, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <TextField
                            label={`${t('automation.automation_bot.add.button_text') || 'متن دکمه'} ${index + 1}`}
                            variant="outlined"
                            value={button.text}
                            onChange={(e) => {
                              const newButtons = [...editButtons];
                              newButtons[index].text = e.target.value;
                              setEditButtons(newButtons);
                            }}
                            sx={{ width: '100%', mb: 2 }}
                          />
                          <TextField
                            label={`${t('automation.automation_bot.add.button_url') || 'آدرس دکمه'} ${index + 1}`}
                            variant="outlined"
                            value={button.url}
                            onChange={(e) => {
                              const newButtons = [...editButtons];
                              newButtons[index].url = e.target.value;
                              setEditButtons(newButtons);
                            }}
                            sx={{ width: '100%' }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
              </form>
            </Box>
            <Box sx={{ width: {xs: '100%', sm: '100%', md: '50%'}, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              <Box sx={{
                width: {xs: '100%', sm: '100%', md: '60%'} , 
                height: '100%' , 
                minHeight: {xs: '200px', sm: '250px', md: '300px'}, 
                minWidth: {xs: '100%', sm: '340px', md: '420px'} , 
                maxWidth: {xs: '560px', sm: '520px', md: '680px'},
                borderRadius: '20px', 
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)', 
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: {xs: 1, sm: 1.25, md: 1.5}, backgroundColor: '#009688'}}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' , textAlign: 'center', mb: 0, color: 'white', fontSize: {xs: '0.9rem', sm: '1rem', md: '1.25rem'}}}>
                    {t('automation.automation_bot.preview.title')}
                  </Typography>
                </Box>
                <Box sx={{mt: {xs: 2, sm: 3, md: 4}, ml: {xs: 1, sm: 1.5, md: 2}, pr: {xs: 2, sm: 3, md: 5}}}>
                  <Box sx={{p: {xs: 1, sm: 1.25, md: 1.5}, backgroundColor: '#009688', width: 'max-content', color: 'white', borderRadius: '10px 10px 10px 0', maxWidth: {xs: '85%', sm: '85%', md: '80%'}, fontSize: {xs: '0.85rem', sm: '0.9rem', md: '1rem'}, wordBreak: 'break-word', overflowWrap: 'break-word', overflow: 'hidden'}}>
                    {editConditionType === 'contains' ? `...${editTriggerValue || ''}...` : (editTriggerValue || '')}
                  </Box>
                </Box>
                <Box sx={{mt: {xs: 1, sm: 1.5, md: 2}, mr: {xs: 1, sm: 2, md: 3}, display: 'flex', justifyContent: 'flex-end', alignItems: 'end', pl: {xs: 2, sm: 4, md: 6}, pb: {xs: 2, sm: 3, md: 4}}}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 1, p: {xs: 1, sm: 1.25, md: 1.5}, backgroundColor: '#f5f5f5', width: 'max-content', color: 'black', borderRadius: '10px 10px 0px 10px', maxWidth: {xs: '75%', sm: '85%', md: '90%'}, fontSize: {xs: '0.85rem', sm: '0.9rem', md: '1rem'}, wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'break-spaces', overflow: 'hidden'}}>
                    <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', width: '100%', alignSelf: 'stretch' }}>
                      {(editResponseMessage || '').replace(/\\n/g, '\n')}
                    </Box>
                    {editHasButtonInResponse && editButtons.map((button, index) => (
                      button.text.trim() && button.url.trim() && (
                        <Button key={index} href={button.url} target="_blank" sx={{width: '100%', color:'black', border:'1px solid black' , fontWeight:'normal'}}>
                          {button.text}
                        </Button>
                      )
                    ))}
                  </Box>
                  <img src="/images/main.png" alt="" style={{width: 'clamp(24px, 5vw, 34px)', height: 'clamp(30px, 6vw, 42px)', marginRight: '10px', marginTop: '8px'}} />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ backgroundColor: 'error.main' , color: 'white',
              '&:hover': {
                backgroundColor: 'error.main',
              }
            }}
            onClick={() => {
              setShowEditModal(false);
              setEditingEntry(null);
              // Reset EDIT form
              setEditDirectChecked(false);
              setEditCommentChecked(false);
              setEditConditionType('equals');
              setEditTriggerValue('');
              setEditResponseMessage('');
              setEditHasButtonInResponse(false);
              setEditButtons([{text: '', url: ''}]);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            sx={{ backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.main',
              }
            }}
            onClick={handleUpdateCommand}
            variant="contained"
            disabled={updatingResponse || !isEditButtonEnabled}
          >
            {updatingResponse ? <CircularProgress size={24} /> : t('automation.automation_bot.edit.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flush Responses Confirmation Dialog */}
      <Dialog open={showFlushDialog} onClose={() => setShowFlushDialog(false)}>
        <DialogTitle>{t('automation.automation_bot.flush.confirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('automation.automation_bot.flush.confirmDescription')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFlushDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleFlush}
            color="error"
            variant="contained"
            disabled={flushing}
          >
            {flushing ? <CircularProgress size={24} /> : t('automation.automation_bot.flush.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const AutomateResponse: FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_autoResponse}>
      <VideoProvider videoUrl={videoUrls.autoResponse} videoTitle={videoTitles.autoResponse}>
        <AutomateResponseContent />
      </VideoProvider>
    </TourProvider>
  );
};

export default AutomateResponse; 